

import React, { useState, useEffect } from 'react';
import medianData from '../../json/median_income.json';
import breakdown from '../../json/expense_breakdown.json';
import housing from '../../json/housing_expenses.json';
import health from '../../json/out_of_pocket_health.json';
import transportData from '../../json/regional_transportation_expenses.json';
import { loadMeansTestStandards } from '../../utils/meansTestStandards';
import { sortDebtorsPrimaryFirst } from '../../utils/debtors';

const primaryIncomeFirst = (items = []) => [...items].sort((left, right) => (
  Number(Boolean(right?.debtor?.is_primary)) - Number(Boolean(left?.debtor?.is_primary))
));

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona',
  AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
  MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska',
  NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  PR: 'Puerto Rico', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia'
};
function getTransportationExpense(stateAbbrev, countyName, vehicleCount) {
  // Convert abbreviation to full name
  const stateFull = STATE_NAMES[stateAbbrev] || stateAbbrev;
  // Find the region for this state
  const regionCode = Object.keys(transportData).find(rc =>
    transportData[rc].states.includes(stateFull)
  );
  if (!regionCode) return 0;
  const region = transportData[regionCode];
  // Zero vehicles => public transportation
  if (vehicleCount === 0) {
    return region.public_transportation || 0;
  }
  // Determine car key: 1 car => one_car; 2+ cars => two_cars
  const carKey = vehicleCount >= 2 ? 'two_cars' : 'one_car';
  // Check MSAs
  const msaEntries = region.operating_costs.msa || {};
  const msaMatch = Object.values(msaEntries).find(msa =>
    Array.isArray(msa.counties) && msa.counties.some(c =>
      c.toLowerCase().includes(countyName.toLowerCase()) ||
      countyName.toLowerCase().includes(c.toLowerCase())
    )
  );
  if (msaMatch && msaMatch[carKey] != null) {
    return msaMatch[carKey];
  }
  // Fallback to regional average
  return (region.operating_costs.region && region.operating_costs.region[carKey]) || 0;
}
function getVehicleOwnershipExpense(stateAbbrev, countyName, vehicleCount) {
  // Convert abbreviation to full name
  const stateFull = STATE_NAMES[stateAbbrev] || stateAbbrev;
  // Find the region for this state
  const regionCode = Object.keys(transportData).find(rc =>
    transportData[rc].states.includes(stateFull)
  );
  if (!regionCode) return 0;
  const region = transportData[regionCode];
  // Zero vehicles => public transportation
  if (vehicleCount === 0) {
    return 0;
  }
  // Determine car key: 1 car => one_car; 2+ cars => two_cars
  const carKey = vehicleCount >= 2 ? 'two_cars' : 'one_car';

  // Fallback to regional average
  return (region.ownership_costs[carKey]) || 0;
}
function getHousingExpenses(stateAbbrev, countyName, size) {
  const entries = housing[stateAbbrev] || [];
  // loose match county
  console.log(countyName)
  const match = entries.find(e =>
    e.county.toLowerCase().includes(countyName.toLowerCase()) ||
    countyName.toLowerCase().includes(e.county.toLowerCase())
  ) || entries[0] || { expenses: {} };
  const key = String(size > 5 ? 5 : size);
  const data = match.expenses[key] || {};
  return {
    mortgage: data.mortgage || 0,
    non_mortgage: data.non_mortgage || 0
  };
}
// Helper to display debtor label or 'Spouse' for non-filing spouse
const getDebtorLabel = (id, caseData) => {
  console.log(caseData)
  console.log(id)
  const d = caseData?.debtors?.find(x => x.id === id);
  if (!d) return '';
  return d.is_filing
    ? [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ')
    : 'Spouse';
};

export default function MonthlyIncomeSection({
  caseId,
  caseData,
  dependents = [],
  onMaritalAdjustmentsChange,
  onMeansTestIncomesChange,
}) {
  const API_URL = process.env.REACT_APP_API_URL;
  const TOKEN = localStorage.getItem('accessToken');
const [debtorIncomes, setDebtorIncomes] = useState([]);
const [loading, setLoading] = useState(true);
const [standardsLoading, setStandardsLoading] = useState(true);
const [standardsWarning, setStandardsWarning] = useState('');
// Marital Adjustments state
const [maritalAdjustments, setMaritalAdjustments] = useState([]);
const [newMarital, setNewMarital] = useState({ description: '', amount: '' });
// State for new "Other Income" entries
const [newOtherInputs, setNewOtherInputs] = useState({});
const orderedCaseDebtors = sortDebtorsPrimaryFirst(caseData?.debtors);

// Handler for new "Other Income" input changes
const handleNewOtherChange = (incomeId, field, value) => {
  setNewOtherInputs(prev => ({
    ...prev,
    [incomeId]: {
      ...(prev[incomeId] || {}),
      [field]: value
    }
  }));
};

// Add new "Other Income" with inputs
const handleAddOtherIncome = async (incomeId) => {
  const { description = '', amount = 0 } = newOtherInputs[incomeId] || {};
  try {
    const res = await fetch(
      `${API_URL}/case/${caseId}/means-test/incomes/${incomeId}/other/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify({ description, amount: parseFloat(amount) }),
      }
    );
    if (res.ok) {
      // Clear inputs for this income
      setNewOtherInputs(prev => ({ ...prev, [incomeId]: { description: '', amount: '' } }));
      fetchIncomes();
    }
  } catch (err) {
    console.error(err);
  }
};

const handleDeleteOtherIncome = async (incomeId, otherId) => {
  if (!window.confirm("Delete this other income?")) return;
  try {
    const res = await fetch(
      `${API_URL}/case/${caseId}/means-test/incomes/${incomeId}/other/${otherId}/`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${TOKEN}` },
      }
    );
    if (res.ok) fetchIncomes();
  } catch (err) {
    console.error(err);
  }
};

const handleChangeOtherIncome = (incomeId, otherId, field, value) => {
  setDebtorIncomes(prev => {
    const next = prev.map(item =>
      item.id === incomeId
        ? {
            ...item,
            other_incomes: item.other_incomes.map(oi =>
              oi.id === otherId ? { ...oi, [field]: value } : oi
            ),
          }
        : item
    );
    onMeansTestIncomesChange?.(next);
    return next;
  });
};

const handleSaveOtherIncome = async (incomeId, otherIncome) => {
  try {
    const res = await fetch(
      `${API_URL}/case/${caseId}/means-test/incomes/${incomeId}/other/${otherIncome.id}/`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify(otherIncome),
      }
    );
    if (!res.ok) throw new Error("Save failed");
  } catch (err) {
    console.error(err);
  }
};
  const fetchIncomes = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/case/${caseId}/means-test/incomes/`,
        { headers: { Authorization: `Bearer ${TOKEN}` } }
      );
      if (!res.ok) throw new Error('Failed to load incomes');
      const data = await res.json();
      const orderedData = primaryIncomeFirst(data);
      setDebtorIncomes(orderedData);
      onMeansTestIncomesChange?.(orderedData);
      // Fetch marital adjustments
      const adjRes = await fetch(
        `${API_URL}/case/${caseId}/means-test/marital-adjustments/`,
        { headers: { Authorization: `Bearer ${TOKEN}` } }
      );
      if (adjRes.ok) {
        const adjustments = await adjRes.json();
        setMaritalAdjustments(adjustments);
        onMaritalAdjustmentsChange?.(adjustments);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  // Marital Adjustments handlers
  const handleNewMaritalChange = (field, value) =>
    setNewMarital(prev => ({ ...prev, [field]: value }));

  const handleAddMarital = async () => {
    const res = await fetch(
      `${API_URL}/case/${caseId}/means-test/marital-adjustments/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`
        },
        body: JSON.stringify(newMarital)
      }
    );
    if (res.ok) {
      setNewMarital({ description: '', amount: '' });
      fetchIncomes(); // re-fetch incomes & adjustments
    }
  };

  const handleDeleteMarital = async (id) => {
    if (!window.confirm('Delete this adjustment?')) return;
    const res = await fetch(
      `${API_URL}/case/${caseId}/means-test/marital-adjustments/${id}/`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${TOKEN}` }
      }
    );
    if (res.ok) fetchIncomes();
  };

  const handleChangeMarital = (id, field, value) => {
    setMaritalAdjustments(prev => {
      const next = prev.map(adj =>
        adj.id === id ? { ...adj, [field]: value } : adj
      );
      onMaritalAdjustmentsChange?.(next);
      return next;
    });
  };

  const handleSaveMarital = async (adj) => {
    await fetch(
      `${API_URL}/case/${caseId}/means-test/marital-adjustments/${adj.id}/`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`
        },
        body: JSON.stringify(adj)
      }
    );
  };

  useEffect(() => {
    fetchIncomes();
  }, [caseId]);

  useEffect(() => {
    let isMounted = true;

    loadMeansTestStandards()
      .then(() => {
        if (!isMounted) return;
        setStandardsWarning('');
        setStandardsLoading(false);
      })
      .catch((error) => {
        if (!isMounted) return;
        setStandardsWarning(
          `Could not load the active means-test standards. Showing the bundled fallback values. ${error.message || ''}`.trim()
        );
        setStandardsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (id, field, value) => {
    setDebtorIncomes(prev => {
      const next = prev.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        // Auto-calc business net (5c)
        const bizGross = parseFloat(updated.biz_gross_receipts) || 0;
        const bizExp = parseFloat(updated.biz_expenses) || 0;
        updated.biz_net_monthly = bizGross - bizExp;
        // Auto-calc real property net (6c)
        const realGross = parseFloat(updated.real_gross_receipts) || 0;
        const realExp = parseFloat(updated.real_expenses) || 0;
        updated.real_net_monthly = realGross - realExp;
        return updated;
      });
      onMeansTestIncomesChange?.(next);
      return next;
    });
  };

  const handleSave = async (income) => {
    try {
      const res = await fetch(
        `${API_URL}/case/${caseId}/means-test/incomes/${income.id}/`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${TOKEN}`
          },
          body: JSON.stringify(income)
        }
      );
      if (!res.ok) throw new Error('Save failed');
      // Optionally refresh or show a success message
    } catch (err) {
      console.error(err);
    }
  };

  const handleInitialize = async () => {
    if (!caseData?.debtors) return;
    setLoading(true);
    try {
      for (const debtor of caseData.debtors) {
        const payload = {
          debtor: debtor.id,
          gross_wages: 0,
          alimony: 0,
          household_expenses: 0,
          biz_gross_receipts: 0,
          biz_expenses: 0,
          biz_net_monthly: 0,
          real_gross_receipts: 0,
          real_expenses: 0,
          real_net_monthly: 0,
          interest_dividends: 0,
          unemployment: 0,
          pension: 0
        };
        await fetch(
          `${API_URL}/case/${caseId}/means-test/incomes/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${TOKEN}`
            },
            body: JSON.stringify(payload)
          }
        );
      }
      await fetchIncomes();
    } catch (err) {
      console.error(err);
    }
  };

  // Handler: Populate from Income Data (Enhanced: Exclude Social Security/VA, include other incomes/gov assistances)
  const handlePopulateFromIncome = async () => {
    if (!caseData?.debtors) return;
    // 1) Update main income fields
    const updated = debtorIncomes.map(item => {
      // Change: match debtor by id (not item.debtor.id, but item.debtor)
      const debtorData = caseData.debtors.find(d => d.id === item.debtor);
      if (!debtorData) return item;
      const newItem = { ...item };
      // Gross wages minus deductions
      let gross = 0;
      debtorData.employments.forEach(emp => {
        let empGross =
          emp.pay_type === 'hourly'
            ? (parseFloat(emp.pay_rate) || 0) * (parseFloat(emp.pay_units) || 0) * 4.33
            : parseFloat(emp.pay_rate) || 0;
        const deductionsSum = (emp.deductions || []).reduce(
          (sum, d) => sum + (parseFloat(d.amount) || 0),
          0
        );
        gross += empGross;
      });
      newItem.gross_wages = gross.toFixed(2);
      // Alimony
      newItem.alimony = parseFloat(debtorData.family_support_payments) || 0;
      // Business lines
      const bizGross = debtorData.business_incomes.reduce(
        (sum, b) => sum + (parseFloat(b.gross_revenue) || 0), 0
      );
      const bizExp = debtorData.business_incomes.reduce(
        (sum, b) => sum + b.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0), 0
      );
      newItem.biz_gross_receipts = bizGross.toFixed(2);
      newItem.biz_expenses = bizExp.toFixed(2);
      newItem.biz_net_monthly = (bizGross - bizExp).toFixed(2);
      // Real property left
      newItem.real_gross_receipts = 0;
      newItem.real_expenses = 0;
      newItem.real_net_monthly = '0.00';
      // Other fixed incomes (exclude Social Security)
      newItem.interest_dividends = parseFloat(debtorData.interest_dividend_income) || 0;
      newItem.unemployment = parseFloat(debtorData.unemployment_income) || 0;
      newItem.pension = parseFloat(debtorData.pension_income) || 0;
      return newItem;
    });
    setDebtorIncomes(updated);
    onMeansTestIncomesChange?.(updated);
    // Save updated main incomes
    await Promise.all(updated.map(inc => handleSave(inc)));

    // 2) Populate Other Income items
    for (const item of updated) {
      const debtorData = caseData.debtors.find(d => d.id === item.debtor);
      if (!debtorData) continue;
      // Filter other incomes, exclude VA/Veterans
      const sources = [
        ...(debtorData.other_income_items || []),
        ...(debtorData.gov_assistances || [])
      ].filter(src => !/va|veterans/i.test(src.name));
      // Create each via API
      for (const src of sources) {
        await fetch(
          `${API_URL}/case/${caseId}/means-test/incomes/${item.id}/other/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${TOKEN}`
            },
            body: JSON.stringify({
              description: src.name,
              amount: parseFloat(src.amount)
            })
          }
        );
      }
    }
    // Refresh data
    fetchIncomes();
  };
function getAge(dobStr) {
  const dob = new Date(dobStr);
  const diffMs = Date.now() - dob.getTime();
  const ageDt = new Date(diffMs);
  return Math.abs(ageDt.getUTCFullYear() - 1970);
}
  const stateCode = orderedCaseDebtors[0]?.state || '';
  console.log(caseData)
  const householdSize = (caseData?.debtors?.length || 0) + dependents.length
  console.log(householdSize)
  const foodClothingOther = (breakdown["Miscellaneous"]?.[householdSize] || 0) + (breakdown["Food"]?.[householdSize] || 0) + (breakdown["Housekeeping supplies"]?.[householdSize] || 0) + (breakdown["Apparel & services"]?.[householdSize] || 0) + (breakdown["Personal care products & services"]?.[householdSize] || 0)
  console.log(foodClothingOther)
  const { mortgage, non_mortgage } = getHousingExpenses(
  orderedCaseDebtors[0]?.state,
  orderedCaseDebtors[0]?.county,
  householdSize
);
  const transportAmt1Vehicle = getTransportationExpense(
      orderedCaseDebtors[0]?.state,
      orderedCaseDebtors[0]?.county,
              1
            );
  const transportAmt2Vehicle = getTransportationExpense(
  orderedCaseDebtors[0]?.state,
  orderedCaseDebtors[0]?.county,
    2
  );
    const ownershipAmt1Vehicle = getVehicleOwnershipExpense(
      orderedCaseDebtors[0]?.state,
      orderedCaseDebtors[0]?.county,
              1
            );
  const owernshipAmt2Vehicle = getVehicleOwnershipExpense(
  orderedCaseDebtors[0]?.state,
  orderedCaseDebtors[0]?.county,
    2
  );
  const ages = [];
  let dob1 = orderedCaseDebtors[0]?.dob || "";
  if (!dob1) {
    ages.push(30);
  } else {
      ages.push(getAge(dob1));
    }
  if (caseData?.debtors?.length === 2){
    let dob2 = caseData?.debtors?.[1]?.dob || dob1;
    ages.push(getAge(dob2))
  }

  // Include dependents' ages
  (dependents || []).forEach(d => ages.push(d.age));
  // Sum out-of-pocket per person
  const medTotal = ages.reduce((sum, age) => {
    const key = age >= 65 ? '65_and_older' : 'under_65';
    return sum + (health[key] || 0);
  }, 0);
  const medianIncome = medianData[STATE_NAMES[stateCode]]?.[`${householdSize}`] || 'N/A';
const totalMonthly = income => {
  if (!income) return 0;
  const otherSum = income.other_incomes?.reduce((sum, oi) => sum + (parseFloat(oi.amount) || 0), 0) || 0;
  return ['gross_wages','alimony','household_expenses','biz_net_monthly','real_net_monthly','interest_dividends','unemployment','pension']
    .reduce((sum, key) => sum + (parseFloat(income[key]) || 0), otherSum);
};

const totalDebtor1 = totalMonthly(debtorIncomes[0]).toFixed(2);
const totalDebtor2 = totalMonthly(debtorIncomes[1]).toFixed(2);
const combinedMonthly = (parseFloat(totalDebtor1) + parseFloat(totalDebtor2)).toFixed(2);
const combinedYearly = (parseFloat(combinedMonthly) * 12).toFixed(2);
  if (loading || standardsLoading) {
    return <p>Loading monthly income and current means-test standards…</p>;
  }
  if (!caseData || !caseData.debtors) {
    return <p>Loading case data…</p>;
  }

  return (
    <div className="bg-white rounded-lg shadow-2xl p-6 mx-4 sm:mx-auto sm:max-w-[1200px] mt-8">
      <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">3. Monthly Income</h3>
      {standardsWarning && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {standardsWarning}
        </div>
      )}
      {debtorIncomes.length === 0 && !loading && (
        <div className="mb-4">
          <p className="text-sm text-gray-700">
            No income data yet. Initialize to create entries:
          </p>
          <button
            onClick={handleInitialize}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded"
          >
            Initialize Monthly Income
          </button>
        </div>
      )}
      {debtorIncomes.length > 0 && (
        <>
          <button
            onClick={handlePopulateFromIncome}
            className="mb-4 px-4 py-2 bg-green-600 text-white rounded"
          >
            Populate from Income Data
          </button>
          <table className="w-full table-fixed border border-gray-300 mb-8">
          <thead>
            <tr className="bg-gray-100">
              <th className="w-1/2 px-2 py-2 border border-gray-300 text-left">Line Description</th>
              <th className="w-1/4 px-2 py-2 border border-gray-300 text-left">
                {debtorIncomes[0]?.debtor.first_name}
              </th>
              <th className="w-1/4 px-2 py-2 border border-gray-300 text-left">
                {debtorIncomes[1]?.debtor.first_name}
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              ['gross_wages', '2. Gross wages, salary, tips, bonuses, overtime, and commissions'],
              ['alimony', '3. Alimony and maintenance payments'],
              ['household_expenses', '4. Household expenses contributions'],
              ['biz_gross_receipts', '5a. Business gross receipts'],
              ['biz_expenses', '5b. Business operating expenses'],
              ['biz_net_monthly', '5c. Business net monthly income'],
              ['real_gross_receipts', '6a. Real property gross receipts'],
              ['real_expenses', '6b. Real property operating expenses'],
              ['real_net_monthly', '6c. Real property net monthly income'],
              ['interest_dividends', '7. Interest, dividends, and royalties'],
              ['unemployment', '8. Unemployment compensation'],
              ['pension', '9. Pension or retirement income']
            ].map(([field, label]) => (
              <tr
                key={field}
                className={`border-t border-gray-300 ${
                  (['biz_gross_receipts','biz_expenses','real_gross_receipts','real_expenses'].includes(field))
                    ? 'bg-green-50 border-l-4 border-green-300'
                    : ''
                }`}
              >
                <td
                  className={`px-2 py-2 border border-gray-300 text-sm ${
                    (['biz_gross_receipts','biz_expenses','real_gross_receipts','real_expenses'].includes(field))
                      ? 'pl-6 italic'
                      : ''
                  }`}
                >
                  {label}
                </td>
                {debtorIncomes[0] ? (
                  <td className="px-2 py-2 border border-gray-300">
                    <input
                      type="number"
                      value={debtorIncomes[0]?.[field] ?? ''}
                      onChange={e => handleChange(debtorIncomes[0].id, field, e.target.value)}
                      onBlur={() => handleSave(debtorIncomes[0])}
                      className={`w-full border border-gray-300 rounded px-1 py-1${
                        (field === 'biz_net_monthly' || field === 'real_net_monthly')
                          ? ' bg-gray-100'
                          : ''
                      }`}
                      {...((field === 'biz_net_monthly' || field === 'real_net_monthly') ? { readOnly: true } : {})}
                    />
                  </td>
                ) : (
                  <td className="px-2 py-2 border border-gray-300">
                    <input type="number" value="" disabled className="w-full border border-gray-300 rounded px-1 py-1 bg-gray-100" />
                  </td>
                )}
                {debtorIncomes[1] ? (
                  <td className="px-2 py-2 border border-gray-300">
                    <input
                      type="number"
                      value={debtorIncomes[1]?.[field] ?? ''}
                      onChange={e => handleChange(debtorIncomes[1].id, field, e.target.value)}
                      onBlur={() => handleSave(debtorIncomes[1])}
                      className={`w-full border border-gray-300 rounded px-1 py-1${
                        (field === 'biz_net_monthly' || field === 'real_net_monthly')
                          ? ' bg-gray-100'
                          : ''
                      }`}
                      {...((field === 'biz_net_monthly' || field === 'real_net_monthly') ? { readOnly: true } : {})}
                    />
                  </td>
                ) : (
                  <td className="px-2 py-2 border border-gray-300">
                    <input type="number" value="" disabled className="w-full border border-gray-300 rounded px-1 py-1 bg-gray-100" />
                  </td>
                )}
              </tr>
            ))}
            <tr>
              <td className="px-2 py-2 border border-gray-300 text-sm">10. Other Income</td>
              {/* Debtor 1 Other Income CRUD */}
              {debtorIncomes[0] ? (
                <td className="px-2 py-2 border border-gray-300 align-top">
                  {debtorIncomes[0]?.other_incomes?.map(oi => (
                    <div key={oi.id} className="flex items-center space-x-2 mb-1">
                      <input
                        type="text"
                        value={oi.description}
                        onChange={e => handleChangeOtherIncome(debtorIncomes[0].id, oi.id, 'description', e.target.value)}
                        onBlur={() => handleSaveOtherIncome(debtorIncomes[0].id, oi)}
                        className="flex-1 border border-gray-300 rounded px-1 py-1 text-sm"
                        placeholder="Description"
                      />
                      <input
                        type="number"
                        value={oi.amount}
                        onChange={e => handleChangeOtherIncome(debtorIncomes[0].id, oi.id, 'amount', e.target.value)}
                        onBlur={() => handleSaveOtherIncome(debtorIncomes[0].id, oi)}
                        className="w-20 border border-gray-300 rounded px-1 py-1 text-sm"
                        placeholder="Amount"
                      />
                      <button
                        onClick={() => handleDeleteOtherIncome(debtorIncomes[0].id, oi.id)}
                        className="text-red-600 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="text"
                      placeholder="Description"
                      value={newOtherInputs[debtorIncomes[0]?.id]?.description || ''}
                      onChange={e => handleNewOtherChange(debtorIncomes[0].id, 'description', e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-1 py-1 text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={newOtherInputs[debtorIncomes[0]?.id]?.amount || ''}
                      onChange={e => handleNewOtherChange(debtorIncomes[0].id, 'amount', e.target.value)}
                      className="w-20 border border-gray-300 rounded px-1 py-1 text-sm"
                    />
                    <button
                      onClick={() => handleAddOtherIncome(debtorIncomes[0].id)}
                      className="text-blue-600 text-sm"
                    >
                      + Add
                    </button>
                  </div>
                </td>
              ) : (
                <td className="px-2 py-2 border border-gray-300 align-top"></td>
              )}
              {/* Debtor 2 Other Income CRUD */}
              {debtorIncomes[1] ? (
                <td className="px-2 py-2 border border-gray-300 align-top">
                  {debtorIncomes[1]?.other_incomes?.map(oi => (
                    <div key={oi.id} className="flex items-center space-x-2 mb-1">
                      <input
                        type="text"
                        value={oi.description}
                        onChange={e => handleChangeOtherIncome(debtorIncomes[1].id, oi.id, 'description', e.target.value)}
                        onBlur={() => handleSaveOtherIncome(debtorIncomes[1].id, oi)}
                        className="flex-1 border border-gray-300 rounded px-1 py-1 text-sm"
                        placeholder="Description"
                      />
                      <input
                        type="number"
                        value={oi.amount}
                        onChange={e => handleChangeOtherIncome(debtorIncomes[1].id, oi.id, 'amount', e.target.value)}
                        onBlur={() => handleSaveOtherIncome(debtorIncomes[1].id, oi)}
                        className="w-20 border border-gray-300 rounded px-1 py-1 text-sm"
                        placeholder="Amount"
                      />
                      <button
                        onClick={() => handleDeleteOtherIncome(debtorIncomes[1].id, oi.id)}
                        className="text-red-600 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="text"
                      placeholder="Description"
                      value={newOtherInputs[debtorIncomes[1]?.id]?.description || ''}
                      onChange={e => handleNewOtherChange(debtorIncomes[1].id, 'description', e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-1 py-1 text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={newOtherInputs[debtorIncomes[1]?.id]?.amount || ''}
                      onChange={e => handleNewOtherChange(debtorIncomes[1].id, 'amount', e.target.value)}
                      className="w-20 border border-gray-300 rounded px-1 py-1 text-sm"
                    />
                    <button
                      onClick={() => handleAddOtherIncome(debtorIncomes[1].id)}
                      className="text-blue-600 text-sm"
                    >
                      + Add
                    </button>
                  </div>
                </td>
              ) : (
                <td className="px-2 py-2 border border-gray-300 align-top"></td>
              )}
            </tr>
          </tbody>
            </table>
            {/* Marital Adjustments Section */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-3">Marital Adjustments</h3>
              <div className="space-y-2 mb-3">
                {maritalAdjustments.map(adj => (
                  <div key={adj.id} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={adj.description}
                      onChange={e => handleChangeMarital(adj.id, 'description', e.target.value)}
                      onBlur={() => handleSaveMarital(adj)}
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Description"
                    />
                    <input
                      type="number"
                      value={adj.amount}
                      onChange={e => handleChangeMarital(adj.id, 'amount', e.target.value)}
                      onBlur={() => handleSaveMarital(adj)}
                      className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Amount"
                    />
                    <button
                      onClick={() => handleDeleteMarital(adj.id)}
                      className="text-red-600 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
              {/* New Adjustment */}
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  placeholder="Description"
                  value={newMarital.description}
                  onChange={e => handleNewMaritalChange('description', e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={newMarital.amount}
                  onChange={e => handleNewMaritalChange('amount', e.target.value)}
                  className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <button
                  onClick={handleAddMarital}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                >
                  + Add
                </button>
              </div>
            </div>
            <div className="mt-4 border-t pt-4 space-y-2">
                <p className="font-medium">
                Debtor 1 Total Monthly Income: ${totalDebtor1}
                </p>
                <p className="font-medium">
                Debtor 2 Total Monthly Income: ${totalDebtor2}
                </p>
                <p className="font-medium">
                Combined Monthly Income: ${combinedMonthly}
                </p>
                <p className="font-medium">
                Combined Yearly Income: ${combinedYearly}
                </p>
                      {caseData && (
        <div className="mb-4">
          <p className="text-sm text-gray-700">
            Median Income for {STATE_NAMES[stateCode] || stateCode} (Household size {householdSize}): ${medianIncome}
          </p>
        </div>
      )}
                {parseFloat(combinedYearly) > parseFloat(medianIncome) && (
                  <div>
                <p className="text-red-600 font-semibold">
                    Combined yearly income ${combinedYearly} exceeds median income ${medianIncome}. Long form is required.
                </p>
                <p className="text-red-600 font-semibold">
                    Food Clothing and Other Expenses ${foodClothingOther}.
                </p>
                  <p className="text-red-600 font-semibold">
                    Out of Pocket Standards: Over 65: ${health['65_and_older']}; Under 65: ${health['under_65']}
                </p>
                <p className="text-red-600 font-semibold pl-6">
                    Out of Pocket Health: ${medTotal} for ages {ages.join(", ")}.
                </p>
                  <p className="text-red-600 font-semibold">
                    Non-mortgage expense: ${non_mortgage}
                </p>
                                  <p className="text-red-600 font-semibold">
                    Mortgage expense: ${mortgage}
                </p>
                  <p className="text-red-600 font-semibold">
                    Transportation expense (1 vehicle): ${transportAmt1Vehicle}
                </p>
                <p className="text-red-600 font-semibold">
                    Transportation expense (2 vehicles): ${transportAmt2Vehicle}
                </p>
                  <p className="text-red-600 font-semibold">
                    Ownership expense (1 vehicle): ${ownershipAmt1Vehicle}
                </p>
                <p className="text-red-600 font-semibold">
                    Ownership expense (2 vehicles): ${owernshipAmt2Vehicle}
                </p>
                </div>
                )}
                                {parseFloat(combinedYearly) < parseFloat(medianIncome) && (
                <p className="text-green-600 font-semibold">
                    Combined yearly income ${combinedYearly} less than median income ${medianIncome}.
                </p>
                )}
            </div>
        </>
      )}

      {/* Removed Other Income section. See line 10 in table above. */}
    </div>
  );
}
